import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { GlobalHookService } from "../global-hook.service";
import { AuthService } from "../core/core-services/services/auth.service";
import { SessionConfirmationService } from "../core/core-services/services/session-confirmation.service";
import { DialogService } from "../core/core-services/services/dialog.service";
import { WelcomeModalComponent } from "../dashboard/walk-me/welcome-modal/welcome-modal.component";
import { SharedDataService } from "../core/core-services/services/shared-data.service";
import { OnboardingProgressService } from "../core/core-services/services/onboarding-progress.service";
import { VngDialogRef } from "@vengreso/angular-components";
import { Location } from "@angular/common";
import { Router } from "@angular/router";

@Component({
    selector: "app-global-overlay",
    templateUrl: "./global-overlay.component.html",
    styleUrls: ["./global-overlay.component.scss"],
    standalone: false
})
export class GlobalOverlayComponent implements OnInit, OnDestroy {
  private sessionConfirmedSub?: Subscription;

  constructor(
    private readonly globalHookService: GlobalHookService,
    private readonly authService: AuthService,
    // Issue #564 (round 2): providedIn: "root" — a true app-wide singleton.
    // This component is mounted at the app root, so it happens to share
    // AuthService's root-injector instance too, but DashboardComponent
    // (behind the LAZY DashboardModule) does not — see
    // session-confirmation.service.ts for the full explanation of why this
    // signal cannot live on AuthService.
    private readonly sessionConfirmation: SessionConfirmationService,
    private readonly dialog: DialogService,
    public sharedData: SharedDataService,
    private readonly location: Location,
    private readonly router: Router,
    private readonly onboardingProgress: OnboardingProgressService
  ) {}

  ngOnInit(): void {
    this.globalHookService.registerComponent("global", this);

    this.globalHookService.functionCalled$.subscribe(({ functionName, args }) => {
      if (this[functionName]) {
        this[functionName](...args);
      }
    });

    const currentUrl = this.location.path();
    if (currentUrl.includes("session") || currentUrl.includes("onboarding")) {
      return;
    }

    if (!this.user) {
      // Issue #564: no cached userDetails at all — nothing to gate the
      // walk-me modal on, and reading .onboardingv2_step below would throw.
      return;
    }

    // Issue #564: cached userDetails is not proof of a valid session — wait
    // for AuthInterceptor to confirm the first authenticated 2xx round-trip
    // before scheduling the auto-open. With a stale/expired session no such
    // response ever arrives (401 -> refresh fails -> sign-in redirect), so
    // sessionConfirmed$ never emits and the modal never opens.
    this.sessionConfirmedSub = this.sessionConfirmation.sessionConfirmed$.pipe(take(1)).subscribe(() => {
      if (!this.user.onboardingv2_step || this.user.onboardingv2_step < 1) {
        setTimeout(() => {
          if (!this.globalHookService.onboardingActive) {
            this.globalHookService.components.global.startModal();
          }
        }, 2000);
      }
    });
  }

  ngOnDestroy(): void {
    this.sessionConfirmedSub?.unsubscribe();
  }

  get user() {
    return this.authService.userDetails?.user_details;
  }

  // Centralized in OnboardingProgressService (issue #523) — these getters
  // are now thin passthroughs instead of a 3rd copy of the same logic.
  get maxStep() {
    return this.onboardingProgress.maxStep;
  }

  get showFlyLearning() {
    return this.onboardingProgress.showFlyLearning;
  }

  /**
   * Issue #520: the modal must not be dismissible via backdrop-click/Escape
   * while the two required steps (install_extension, about_you) aren't done
   * yet — `disableClose` starts at `!requiredComplete` (so an existing user
   * who already has both, per OnboardingProgressService's rollout-compat
   * derivation, sees normal dismissal immediately — no behavior change for
   * them) and is kept live for the rest of this dialog's lifetime by the
   * `requiredComplete$` subscription below, so completing a required step
   * WHILE the modal is open (e.g. the extension gets detected mid-session)
   * unblocks it without the modal being closed/reopened. The X (close)
   * button itself is gated separately, in WalkMeHeaderComponent's template —
   * it simply isn't rendered until requiredComplete, so no corresponding
   * change is needed here for that path. `dialogRef.close()` calls the flow
   * itself makes (e.g. hiding this modal to open the Add-FlyCut dialog, or
   * TestFlycutComponent reopening it) are untouched by disableClose — it
   * only gates the container's own backdrop/Escape handlers — so that
   * choreography keeps working unchanged.
   */
  startModal(step: number = undefined): void {
    // Issue #564 defense-in-depth: every call path (auto-open, header hat,
    // retake-onboarding, the showonboardingprogresstraining deep link) funnels
    // through here, so this is the one place that can categorically refuse to
    // open the modal without cached user details to render (also guards the
    // access below from throwing when step is left undefined).
    if (!this.authService.userDetails?.user_details) {
      return;
    }

    if (step === undefined) {
      step = Math.min(this.authService.userDetails.user_details.onboardingv2_step ?? -1, this.maxStep);
    }

    // Issue #537: this is the single choke point every "open the walk-me
    // modal" call path funnels through, so it's also the right place to
    // tear down the header-hat re-entry tooltip if it happens to be showing
    // — the modal opening makes it moot, and it must not linger over/behind
    // the now-open dialog. Not a dismissal: teardownOnboardingReentryTooltip()
    // does not touch the dismissal counter.
    this.globalHookService.components.topBar?.teardownOnboardingReentryTooltip();

    this.globalHookService.onboardingActive = true;
    const modal = this.dialog.openDialogComponent(
      WelcomeModalComponent,
      { step, class: "fms-walk-me-modal-container" },
      "400px",
      !this.onboardingProgress.requiredComplete
    );

    const requiredCompleteSub = this.onboardingProgress.requiredComplete$.subscribe((requiredComplete) => {
      this.dialog.setDisableClose(modal, !requiredComplete);
    });
    modal.afterClosed().subscribe(() => requiredCompleteSub.unsubscribe());

    this.afterModalIsClosed(modal);
  }

  afterModalIsClosed<T>(modal: VngDialogRef<T>) {
    modal.afterClosed().subscribe({
      next: (data) => {
        if (data?.action === "retake_onboarding") {
          return;
        }
        const currentUrl = this.router.url;

        if (currentUrl === "/flyboard?showonboardingprogresstraining=true") {
          this.location.replaceState("flyboard");
        }
      },
    });
  }
}
